import React, { FC } from 'react';
import { Tooltip } from 'antd';
import AviaryIcon from 'assets/images/icons/aviary.svg';
import LeafIcon from 'assets/images/icons/leaf.svg';
import MtbIcon from 'assets/images/icons/mtb.svg';
import TreesIcon from 'assets/images/icons/trees.svg';
import FlowerIcon from 'assets/images/icons/flower.svg';
import PicnicIcon from 'assets/images/icons/picnic.svg';
import KayakIcon from 'assets/images/icons/kayak.svg';
import BalsaIcon from 'assets/images/icons/balsa.svg';
import GrillIcon from 'assets/images/icons/grill.svg';
import CanopyIcon from 'assets/images/icons/canopy.svg';
import styles from './FeatureIcon.module.scss';

export type FeatureIconVariant = 'dark' | 'light' | 'availability';

interface IconAsset {
  src: string;
  scale: number;
}

// `scale` normalises each file's own padding so every glyph reads at the size aviary does.
const nameToIcon: Record<string, IconAsset> = {
  aviario: { src: AviaryIcon, scale: 1 },
  'jardin-botanico': { src: LeafIcon, scale: 1 },
  'circuito-mtb': { src: MtbIcon, scale: 0.86 },
  'humedal-giverny': { src: TreesIcon, scale: 0.71 },
  'plaza-la-virgen-senderos': { src: FlowerIcon, scale: 0.69 },
  'zonas-picnic-miradores': { src: PicnicIcon, scale: 1 },
  balsa: { src: BalsaIcon, scale: 1 },
  kayak: { src: KayakIcon, scale: 1 },
  grill: { src: GrillIcon, scale: 0.57 },
  canopy: { src: CanopyIcon, scale: 0.6 },
};

const FALLBACK_ICON: IconAsset = { src: LeafIcon, scale: 1 };

const variantToStyle: Record<FeatureIconVariant, string> = {
  dark: styles.Dark,
  light: styles.Light,
  availability: styles.Availability,
};

const DEFAULT_SIZE = 32;
const DEFAULT_VARIANT: FeatureIconVariant = 'dark';

const ICON_TO_CIRCLE_RATIO = 1;

interface FeatureIconProps {
  icon: string;
  description: string;
  variant?: FeatureIconVariant;
  size?: number;
}

const FeatureIcon: FC<FeatureIconProps> = ({
  icon,
  description,
  variant,
  size,
}: FeatureIconProps) => {
  const circleSize = size || DEFAULT_SIZE;
  const asset = nameToIcon[icon] || FALLBACK_ICON;
  const iconSize = Math.round(circleSize * ICON_TO_CIRCLE_RATIO * asset.scale);

  return (
    <Tooltip title={description}>
      <span
        className={`${styles.Circle} ${variantToStyle[variant || DEFAULT_VARIANT]}`}
        style={{ height: circleSize, width: circleSize }}
        role="img"
        aria-label={description}
      >
        <img
          className={styles.Icon}
          src={asset.src}
          style={{ height: iconSize, width: iconSize }}
          alt=""
        />
      </span>
    </Tooltip>
  );
};

FeatureIcon.defaultProps = {
  variant: DEFAULT_VARIANT,
  size: DEFAULT_SIZE,
};

export default FeatureIcon;
